import { CATEGORY_PALETTE } from "@volca/ui/tokens/chart-colors";
import { COLOR_PALETTES } from "@volca/ui/tokens/color-palettes";
import type { StatusColor } from "@volca/ui/tokens/component-colors";

const STATUS_CLASSES = {
  neutral: {
    surface: "bg-neutral",
    foreground: "text-ink",
    border: "border-line",
    hover: "hover:bg-neutral-hovered",
    emphasis: "bg-neutral-hovered",
    solid: "bg-inverse",
    solidForeground: "text-on-inverse",
  },
  brand: {
    surface: "bg-brand-subtle",
    foreground: "text-brand",
    border: "border-brand-line",
    hover: "hover:bg-brand-subtle-hovered",
    emphasis: "bg-brand-subtle-hovered",
    solid: "bg-brand-bold",
    solidForeground: "text-on-brand",
  },
  danger: {
    surface: "bg-danger-subtle",
    foreground: "text-danger",
    border: "border-danger-line",
    hover: "hover:bg-danger-subtle-hovered",
    emphasis: "bg-danger-subtle-hovered",
    solid: "bg-danger-bold",
    solidForeground: "text-on-danger",
  },
  warning: {
    surface: "bg-warning-subtle",
    foreground: "text-warning",
    border: "border-warning-line",
    hover: "hover:bg-warning-subtle-hovered",
    emphasis: "bg-warning-subtle-hovered",
    solid: "bg-warning-bold",
    solidForeground: "text-on-warning",
  },
  success: {
    surface: "bg-success-subtle",
    foreground: "text-success",
    border: "border-success-line",
    hover: "hover:bg-success-subtle-hovered",
    emphasis: "bg-success-subtle-hovered",
    solid: "bg-success-bold",
    solidForeground: "text-on-success",
  },
  information: {
    surface: "bg-information-subtle",
    foreground: "text-information",
    border: "border-information-line",
    hover: "hover:bg-information-subtle-hovered",
    emphasis: "bg-information-subtle-hovered",
    solid: "bg-information-bold",
    solidForeground: "text-on-information",
  },
} as const satisfies Record<StatusColor, Record<string, string>>;

const statusPalette = <
  const T extends StatusColor,
  const P extends (typeof COLOR_PALETTES)[keyof typeof COLOR_PALETTES]["paint"],
>(
  tone: T,
  paint: P,
) => ({
  tone,
  paint,
  ...STATUS_CLASSES[tone],
});

export const REFERRAL_PALETTE = {
  LEAD: COLOR_PALETTES.blue,
  IN_CONTACT: COLOR_PALETTES.cyan,
  SCHEDULED: COLOR_PALETTES.purple,
  CLOSED: statusPalette("success", COLOR_PALETTES.green.paint),
  PAID: statusPalette("neutral", COLOR_PALETTES.sand.paint),
  LOST: statusPalette("danger", COLOR_PALETTES.red.paint),
  UNRESPONSIVE: statusPalette("warning", COLOR_PALETTES.amber.paint),
} as const;

export const AP_PALETTE = {
  neutral: statusPalette("neutral", COLOR_PALETTES.sand.paint),
  processing: statusPalette("information", COLOR_PALETTES.blue.paint),
  complete: statusPalette("success", COLOR_PALETTES.green.paint),
  needsReview: statusPalette("warning", COLOR_PALETTES.amber.paint),
  failed: statusPalette("danger", COLOR_PALETTES.red.paint),
  automated: COLOR_PALETTES.purple,
  job: COLOR_PALETTES.blue,
  vendor: COLOR_PALETTES.cyan,
  pricebookCost: COLOR_PALETTES.amber,
  pricebookItem: COLOR_PALETTES.purple,
  updatePo: COLOR_PALETTES.orange,
  createDocument: COLOR_PALETTES.plum,
  cancelDocument: COLOR_PALETTES.red,
} as const;

export const PAYROLL_PALETTE = {
  manual: COLOR_PALETTES.amber,
  pending: statusPalette("neutral", COLOR_PALETTES.sand.paint),
  processing: statusPalette("information", COLOR_PALETTES.blue.paint),
  completed: statusPalette("success", COLOR_PALETTES.green.paint),
  failed: statusPalette("danger", COLOR_PALETTES.red.paint),
} as const;

export const APPROVAL_PALETTE = {
  pending: statusPalette("neutral", COLOR_PALETTES.sand.paint),
  partial: statusPalette("information", COLOR_PALETTES.blue.paint),
  approved: statusPalette("success", COLOR_PALETTES.green.paint),
  needsAttention: statusPalette("warning", COLOR_PALETTES.amber.paint),
} as const;

export const DELIVERY_PALETTE = {
  pending: statusPalette("neutral", COLOR_PALETTES.sand.paint),
  sent: statusPalette("success", COLOR_PALETTES.green.paint),
  failed: statusPalette("danger", COLOR_PALETTES.red.paint),
} as const;

export const EDITOR_PALETTE = {
  added: statusPalette("success", COLOR_PALETTES.green.paint),
  removed: statusPalette("danger", COLOR_PALETTES.red.paint),
  changed: statusPalette("warning", COLOR_PALETTES.amber.paint),
  format: statusPalette("neutral", COLOR_PALETTES.sand.paint),
  search: statusPalette("information", COLOR_PALETTES.blue.paint),
} as const;

export const PARTICIPATION_PALETTE = {
  signups: COLOR_PALETTES.blue,
  jobsCompleted: COLOR_PALETTES.green,
} as const;

export const PROFITABILITY_PALETTE = {
  result: COLOR_PALETTES.teal.paint,
  loss: COLOR_PALETTES.red.paint,
  costs: [
    CATEGORY_PALETTE.amber,
    CATEGORY_PALETTE.blue,
    CATEGORY_PALETTE.orange,
    CATEGORY_PALETTE.plum,
    CATEGORY_PALETTE.purple,
    CATEGORY_PALETTE.cyan,
    CATEGORY_PALETTE.red,
    CATEGORY_PALETTE.sand,
  ],
} as const;
